import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import React from 'react';
import { SyncCoreApiContext } from '../../../../../../contexts/SyncCoreApiContext';
import { declareTaxonomyTermScope, getCachedTaxonomyTermsByFacets } from './useCachedTaxonomyData';

/**
 * The facets whose terms a screen can reference by key — or undefined where no screen has said.
 *
 * Read by the shared term index: under a scope it loads only the facets named, in one request,
 * instead of every term of every facet the viewer can see. A screen declares its scope once, at the
 * top; the term-rendering components inside it stay exactly as they are.
 */
export const TaxonomyTermIndexScopeContext = React.createContext<readonly TaxonomyFacet[] | undefined>(undefined);

/**
 * Declares which facets the screen inside names, and loads all of them in one request as it mounts.
 *
 * Every reader of tags under the scope — the screen's own facet lists, its filter dropdowns, the
 * shared term index, a module further down — asks through the per-facet cache, and the cache sends
 * the reads of declared facets made in the same pass as one request. The scope asks for its facets
 * in the pass the screen mounts in, so the screen's own reads and the scope's travel together, and a
 * reader that mounts afterwards finds its facet already loaded or on its way. A facet the scope does
 * not declare is asked for on its own, at once — which is how a screen keeps the one small list its
 * first figures wait on out of the larger request.
 *
 * Pass a module-level constant: the index is keyed on the facets' identity as a set, and a new
 * array per render would re-resolve the index on every render.
 *
 * A key outside the declared facets resolves as missing — the same answer the index gives for a
 * deleted term — so a screen has to name every facet its rows can carry.
 */
export function TaxonomyTermIndexScope({ facets, children }: { facets: readonly TaxonomyFacet[]; children: React.ReactNode }) {
  const api = React.useContext(SyncCoreApiContext)?.api;

  // While the scope is on screen, a read of any of its facets — the screen's first reads, a screen
  // asking again after a failed load, a module mounting later — travels with the other reads of its
  // pass and brings the scope's other facets not loaded yet along. Declared as a layout effect: those
  // run before any effect of the screen inside, so its very first reads already travel together.
  React.useLayoutEffect(() => declareTaxonomyTermScope(facets), [facets]);

  React.useEffect(() => {
    if (!api) {
      return;
    }
    // Each reader receives a failure through its own request and shows it where it renders; the
    // scope renders nothing of the terms, so it has nothing to show.
    void getCachedTaxonomyTermsByFacets(api, facets).catch(() => undefined);
  }, [api, facets]);

  return <TaxonomyTermIndexScopeContext.Provider value={facets}>{children}</TaxonomyTermIndexScopeContext.Provider>;
}
